import { gql, useQuery } from '@apollo/client';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Grid } from '@/components/layout/Grid';
import { Callout } from '@/components/notifications/Callout/Callout';
import { Loader } from '@/components/progress/Loader/Loader';
import { useReportError } from '@/hooks/useReportError';
import { useAddeparIntegrationErrorSearchParam } from '@/modules/assetProviderIntegrations/addepar/hooks/useAddeparIntegrationErrorSearchParam';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { OpenAIIntegrationCard } from '@/pages/admin/AdminIntegrationsPage/OpenAIIntegrationCard';
import { englishJoinStringList } from '@/utils/formatting/strings';

import { AddeparIntegrationCard } from './AddeparIntegrationCard';
import { AzureBYOKIntegrationCard } from './AzureBYOKIntegrationCard/AzureBYOKIntegrationCard';
import { BlackDiamondIntegrationCard } from './BlackDiamondIntegrationCard';
import { EmoneyIntegrationCard } from './EmoneyIntegrationCard';
import { useGetIntegrationConfigurationQuery } from './graphql/AdminIntegrationsPage.generated';
import { IntegrationCardContainer } from './IntegrationCardContainer';
import { ManagedSFTPSettings } from './ManagedSFTPSettings';
import { OrionIntegrationCard } from './OrionIntegrationCard';
import { SalesforceIntegrationCard } from './SalesforceIntegrationCard';
import { SnowflakeIntegrationCard } from './SnowflakeIntegrationCard';

const salesforcePermissionQuery = gql`
  query AdminIntegrationsSalesforcePermission {
    currentUser {
      id
      isSuperuser
    }
  }
`;

function formatExpiredIntegrationMessage(names: string[]): string {
  if (names.length === 1) {
    return `Your login to ${names[0]} is expired. Disconnect and relink your integration account to enable valuation updates.`;
  }

  const namesString = englishJoinStringList(names);
  return `Your logins to ${namesString} are expired. Disconnect and relink your integration accounts to enable valuation updates.`;
}

export function AdminIntegrationsPage() {
  const currentUser = useCurrentUser();
  const canAccessProviderIntegrations = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE
  );
  const canManageSFTP = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MANAGE_SFTP
  );
  const { data: salesforcePermission } = useQuery<{
    currentUser: { id: string; isSuperuser: boolean } | null;
  }>(salesforcePermissionQuery, { skip: !canAccessProviderIntegrations });
  const { reportError } = useReportError();
  const { assetIntegrationsEnabled } = useTenantDetailsContext();
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();

  const integrationsDisabled = !assetIntegrationsEnabled;

  const {
    data: integrationData,
    loading: configLoading,
    refetch,
  } = useGetIntegrationConfigurationQuery({
    skip: !canAccessProviderIntegrations,
    onError: (err) => {
      reportError('failed to get integrations data', err);
    },
  });

  useAddeparIntegrationErrorSearchParam();

  const isAddeparConnected =
    integrationData?.isIntegrationUserLoggedInToAddepar?.isLoggedIn ?? false;
  const addeparExternalUserName =
    integrationData?.isIntegrationUserLoggedInToAddepar?.externalUserName ??
    null;

  const isBlackDiamondConnected =
    integrationData?.isIntegrationUserLoggedInToBlackDiamond?.isLoggedIn ??
    false;
  const blackDiamondExternalUserName =
    integrationData?.isIntegrationUserLoggedInToBlackDiamond
      ?.externalUserName ?? null;

  const isOrionConnected =
    integrationData?.isIntegrationUserLoggedInToOrion?.isLoggedIn ?? false;
  const orionExternalUserName =
    integrationData?.isIntegrationUserLoggedInToOrion?.externalUserName ?? null;

  const isEmoneyConnected =
    integrationData?.isIntegrationUserLoggedInToEMoney?.isLoggedIn ?? false;
  const emoneyExternalUserName =
    integrationData?.isIntegrationUserLoggedInToEMoney?.externalUserName ??
    null;

  const expiredNames = currentUser?.expiredIntegrationNames ?? [];

  // Each query returns configs filtered by provider. Pick the first (there should be at most one per provider).
  const activeAzureConfig =
    integrationData?.openaiSdkConfigs?.edges?.[0]?.node ?? null;
  const activeOpenaiConfig =
    integrationData?.openaiSdkConfigsForOpenAI?.edges?.[0]?.node ?? null;

  const isAzureEnabled = activeAzureConfig?.enabled ?? false;
  const isOpenaiEnabled = activeOpenaiConfig?.enabled ?? false;

  return (
    <SubpageLayout heading="Data integrations">
      {canAccessProviderIntegrations && configLoading && <Loader />}
      <Grid container spacing={2}>
        {canAccessProviderIntegrations && !configLoading && (
          <>
            {expiredNames.length > 0 && (
              <Grid item sm={12}>
                <Callout severity="warning">
                  {formatExpiredIntegrationMessage(expiredNames)}
                </Callout>
              </Grid>
            )}
            <Grid item sm={6} lg={4}>
              <IntegrationCardContainer>
                <AddeparIntegrationCard
                  refetch={refetch}
                  configuration={
                    integrationData?.integrationConfiguration ?? null
                  }
                  isLoggedIn={isAddeparConnected}
                  externalUserName={addeparExternalUserName}
                  disabled={integrationsDisabled}
                />
              </IntegrationCardContainer>
            </Grid>
            <Grid item sm={6} lg={4}>
              <IntegrationCardContainer>
                <BlackDiamondIntegrationCard
                  configuration={
                    integrationData?.integrationConfiguration ?? null
                  }
                  isLoggedIn={isBlackDiamondConnected}
                  externalUserName={blackDiamondExternalUserName}
                  disabled={integrationsDisabled}
                />
              </IntegrationCardContainer>
            </Grid>
            <Grid item sm={6} lg={4}>
              <IntegrationCardContainer>
                <OrionIntegrationCard
                  configuration={
                    integrationData?.integrationConfiguration ?? null
                  }
                  isLoggedIn={isOrionConnected}
                  externalUserName={orionExternalUserName}
                  disabled={integrationsDisabled}
                />
              </IntegrationCardContainer>
            </Grid>
            <Grid item sm={6} lg={4}>
              <IntegrationCardContainer>
                <EmoneyIntegrationCard
                  configuration={
                    integrationData?.integrationConfiguration ?? null
                  }
                  isLoggedIn={isEmoneyConnected}
                  externalUserName={emoneyExternalUserName}
                  disabled={integrationsDisabled}
                />
              </IntegrationCardContainer>
            </Grid>
            <Grid item sm={6} lg={4}>
              <IntegrationCardContainer>
                <SnowflakeIntegrationCard
                  configuration={
                    integrationData?.integrationConfiguration ?? null
                  }
                  disabled={integrationsDisabled}
                />
              </IntegrationCardContainer>
            </Grid>
            {aiCapabilitiesEnabled && (
              <Grid item sm={6} lg={4}>
                <IntegrationCardContainer>
                  <OpenAIIntegrationCard
                    openaiConfig={activeOpenaiConfig}
                    otherProviderEnabled={isAzureEnabled}
                    disabled={integrationsDisabled}
                  />
                </IntegrationCardContainer>
              </Grid>
            )}
            {integrationData?.integrationConfiguration &&
              integrationData?.integrationConfiguration?.enableLlmByok &&
              aiCapabilitiesEnabled && (
                <Grid item sm={6} lg={4}>
                  <IntegrationCardContainer>
                    <AzureBYOKIntegrationCard
                      azureConfig={activeAzureConfig}
                      otherProviderEnabled={isOpenaiEnabled}
                      disabled={integrationsDisabled}
                    />
                  </IntegrationCardContainer>
                </Grid>
              )}
            {salesforcePermission?.currentUser?.isSuperuser &&
              integrationData?.integrationConfiguration && (
                <Grid
                  item
                  sm={6}
                  lg={4}
                  sx={{
                    width: '100%',
                    // Fit beside the collapsed admin sidebar (56px) and outer gutter
                    // (16px), even below the shared app shell's minimum width.
                    maxWidth: 'calc(100vw - 72px)',
                  }}
                >
                  <IntegrationCardContainer>
                    <SalesforceIntegrationCard
                      configurationID={
                        integrationData.integrationConfiguration.id
                      }
                    />
                  </IntegrationCardContainer>
                </Grid>
              )}
          </>
        )}
        {canManageSFTP && (
          <Grid item sm={6} lg={4}>
            <IntegrationCardContainer>
              <ManagedSFTPSettings />
            </IntegrationCardContainer>
          </Grid>
        )}
      </Grid>
    </SubpageLayout>
  );
}
